/**
 * Shared-store projections for the L2 workflow wall and L3 operations board.
 *
 * This module owns presentation only. HqProjectionSystem supplies snapshots
 * from Arena's session-owned store, so the boards never poll Portal or open a
 * second transport.
 */
import * as pc from 'playcanvas'
import type { OperationsCostSummary } from '../../api/types'
import { createRenderEntity } from './interiorShared'

export type HqSummaryHealth = 'healthy' | 'degraded' | 'down' | 'unknown'

export interface HqProgramSummary {
  health: HqSummaryHealth
  uptimeSeconds: number | null
  activeRuns: number
  queueDepth: number
  totalRuns: number
  completedRuns: number
  failedRuns: number
  costSummary: OperationsCostSummary | null
}

export type HqSummaryBoardKind = 'workflow' | 'operations'

export const EMPTY_HQ_PROGRAM_SUMMARY: HqProgramSummary = {
  health: 'unknown',
  uptimeSeconds: null,
  activeRuns: 0,
  queueDepth: 0,
  totalRuns: 0,
  completedRuns: 0,
  failedRuns: 0,
  costSummary: null,
}

export function formatSummaryUptime(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds) || seconds < 0) return 'UNKNOWN'
  const whole = Math.floor(seconds)
  const days = Math.floor(whole / 86_400)
  const hours = Math.floor((whole % 86_400) / 3_600)
  const minutes = Math.floor((whole % 3_600) / 60)
  return days > 0
    ? `${days}D ${String(hours).padStart(2, '0')}H ${String(minutes).padStart(2, '0')}M`
    : `${hours}H ${String(minutes).padStart(2, '0')}M`
}

export function formatSummaryUsd(value: number): string {
  if (!Number.isFinite(value) || value < 0) return 'UNAVAILABLE'
  return `$${value.toFixed(4)}`
}

export function formatSummaryTokens(value: number): string {
  if (!Number.isSafeInteger(value) || value < 0) return 'UNAVAILABLE'
  return new Intl.NumberFormat('en-US', { useGrouping: true }).format(value)
}

/** Stable text contract used by both the canvas renderer and unit tests. */
export function summaryBoardLines(
  kind: HqSummaryBoardKind,
  summary: HqProgramSummary,
): readonly string[] {
  if (kind === 'workflow') {
    return [
      'SPRINT / WORKFLOW',
      `SYSTEM ${summary.health.toUpperCase()}`,
      `ACTIVE ${summary.activeRuns}   QUEUED ${summary.queueDepth}`,
      `DONE ${summary.completedRuns}   FAILED ${summary.failedRuns}`,
      `TOTAL RUNS ${summary.totalRuns}`,
    ]
  }
  const costLines = summary.costSummary
    ? [
        `COST ${formatSummaryUsd(summary.costSummary.totalCostUsd)}  EST ${formatSummaryUsd(summary.costSummary.estimatedUsd)}`,
        `TOKENS ${formatSummaryTokens(summary.costSummary.totalTokens)}  ${summary.costSummary.since.toUpperCase()} ${summary.costSummary.windowed ? 'WINDOWED' : 'TOUCHED-LIFETIME'}`,
      ]
    : ['COST / TOKENS UNAVAILABLE', 'WINDOW UNAVAILABLE']
  return [
    'STATE OF ANIMUS',
    `SYSTEM ${summary.health.toUpperCase()}`,
    `UPTIME ${formatSummaryUptime(summary.uptimeSeconds)}`,
    `RUNS ${summary.totalRuns}   ACTIVE ${summary.activeRuns}   QUEUED ${summary.queueDepth}`,
    `DONE ${summary.completedRuns}   FAILED ${summary.failedRuns}`,
    ...costLines,
  ]
}

interface SummaryBoardSpec {
  kind: HqSummaryBoardKind
  name: string
  position: readonly [number, number, number]
  scale: readonly [number, number, number]
}

const BOARD_SPECS: readonly SummaryBoardSpec[] = [
  {
    kind: 'workflow',
    name: 'hq-l2-workflow-summary',
    position: [6.5, 10.15, -10.86],
    // Phase-7 contract: the boardroom presentation wall is 3 m x 1.7 m.
    scale: [3, 1.7, 0.08],
  },
  {
    kind: 'operations',
    name: 'hq-l3-operations-summary',
    position: [-10, 14.9, 11.84],
    scale: [5.4, 2.2, 0.08],
  },
]

interface RenderedSummaryBoard {
  entity: pc.Entity
  project(summary: HqProgramSummary): boolean
  destroy(): void
}

function healthColor(health: HqSummaryHealth): string {
  if (health === 'healthy') return '#63e6a5'
  if (health === 'degraded') return '#f4c66a'
  if (health === 'down') return '#f27878'
  return '#75a9d6'
}

function createSummaryBoard(
  app: pc.Application,
  parent: pc.Entity,
  spec: SummaryBoardSpec,
): RenderedSummaryBoard {
  const material = new pc.StandardMaterial()
  material.diffuse = new pc.Color(0.025, 0.055, 0.075)
  material.emissive = new pc.Color(0.06, 0.22, 0.27)
  material.emissiveIntensity = 0.45
  material.gloss = 0.08
  material.cull = pc.CULLFACE_NONE
  material.update()

  const entity = createRenderEntity({
    parent,
    name: spec.name,
    type: 'box',
    position: spec.position,
    scale: spec.scale,
    material,
  })
  if (entity.render) entity.render.castShadows = false

  let canvas: HTMLCanvasElement | undefined
  let context: CanvasRenderingContext2D | null = null
  let texture: pc.Texture | undefined
  let projectedKey = ''

  try {
    const TextureConstructor = (pc as unknown as { Texture?: unknown }).Texture
    if (typeof document !== 'undefined' && typeof TextureConstructor === 'function') {
      canvas = document.createElement('canvas')
      canvas.width = 768
      canvas.height = 432
      context = canvas.getContext('2d')
      if (context) {
        texture = new pc.Texture(app.graphicsDevice, {
          width: canvas.width,
          height: canvas.height,
          addressU: pc.ADDRESS_CLAMP_TO_EDGE,
          addressV: pc.ADDRESS_CLAMP_TO_EDGE,
        })
        texture.setSource(canvas)
        material.diffuseMap = texture
        material.emissiveMap = texture
        material.emissive = new pc.Color(1, 1, 1)
        material.emissiveIntensity = 0.9
        material.update()
      }
    }
  } catch {
    // Canvas/texture support is optional in headless and unit-test runtimes.
    // The emissive board geometry remains a deliberate visual fallback.
    texture?.destroy()
    texture = undefined
    context = null
  }

  return {
    entity,
    project(summary) {
      const nextKey = JSON.stringify(summary)
      if (nextKey === projectedKey) return false
      projectedKey = nextKey

      const accent = healthColor(summary.health)
      if (context && canvas && texture) {
        const lines = summaryBoardLines(spec.kind, summary)
        context.fillStyle = '#07131c'
        context.fillRect(0, 0, canvas.width, canvas.height)
        context.strokeStyle = accent
        context.lineWidth = 7
        context.strokeRect(18, 18, canvas.width - 36, canvas.height - 36)
        context.textBaseline = 'middle'
        for (const [index, line] of lines.entries()) {
          const title = index === 0
          context.font = title
            ? '700 46px ui-monospace, monospace'
            : '600 27px ui-monospace, monospace'
          context.fillStyle = title ? accent : '#d8edf3'
          context.fillText(line, 42, 65 + index * (spec.kind === 'workflow' ? 72 : 52))
        }
        texture.upload()
      } else {
        const fallback = summary.health === 'down'
          ? new pc.Color(0.75, 0.12, 0.12)
          : summary.health === 'degraded'
            ? new pc.Color(0.62, 0.39, 0.08)
            : new pc.Color(0.06, 0.42, 0.48)
        material.emissive = fallback
        material.emissiveIntensity = summary.activeRuns > 0 ? 1 : 0.45
        material.update()
      }
      return true
    },
    destroy() {
      texture?.destroy()
      texture = undefined
      if (canvas) {
        canvas.width = 0
        canvas.height = 0
      }
      canvas = undefined
      context = null
    },
  }
}

export interface HqSummaryBoardHandles {
  readonly entities: readonly pc.Entity[]
  project(summary: HqProgramSummary): boolean
  destroy(): void
}

export function buildHqSummaryBoards(
  app: pc.Application,
  parent: pc.Entity,
): HqSummaryBoardHandles {
  const boards = BOARD_SPECS.map((spec) => createSummaryBoard(app, parent, spec))
  return {
    entities: boards.map(({ entity }) => entity),
    project(summary) {
      return boards.reduce((changed, board) => board.project(summary) || changed, false)
    },
    destroy() {
      for (const board of boards) board.destroy()
    },
  }
}
